import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useIncomeStatement,
  useFreightProfitability,
  useDriverProfitability,
  useVehicleProfitability,
  type ReportPeriod,
} from '@/hooks/useReports'
import { DataTable } from '@/components/ui/data-table'
import { formatBRL } from '@/lib/currency'
import { cn } from '@/lib/utils'
import type { Column } from '@/components/ui/data-table'

const inputClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

type Tab = 'dre' | 'freight' | 'driver' | 'vehicle'

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'dre', label: 'DRE simplificado' },
  { key: 'freight', label: 'Por frete' },
  { key: 'driver', label: 'Por motorista' },
  { key: 'vehicle', label: 'Por veículo' },
]

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR')
}

function MetricCard({
  label,
  value,
  tone = 'neutral',
  hint,
}: {
  label: string
  value: string
  tone?: 'neutral' | 'green' | 'red'
  hint?: string
}) {
  const toneClass = {
    neutral: 'text-foreground',
    green: 'text-green-700',
    red: 'text-red-700',
  }[tone]
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={cn('mt-2 text-xl font-semibold tabular-nums', toneClass)}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function DRESection({ period }: { period: ReportPeriod }) {
  const { data, isLoading } = useIncomeStatement(period)
  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Carregando…</p>

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricCard label="Entradas no período" value={formatBRL(data.totals.inflow)} tone="green" />
        <MetricCard label="Saídas no período" value={formatBRL(data.totals.outflow)} tone="red" />
        <MetricCard
          label="Resultado do período"
          value={formatBRL(data.totals.net)}
          tone={Number.parseFloat(data.totals.net) >= 0 ? 'green' : 'red'}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border">
          <header className="border-b border-border bg-muted/40 px-4 py-2">
            <h3 className="text-sm font-semibold text-green-700">Receitas</h3>
          </header>
          {data.inflows.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">Sem entradas no período.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.inflows.map((row) => (
                <li key={row.categoryId} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span>
                    {row.categoryName}{' '}
                    <span className="text-xs text-muted-foreground">({row.count})</span>
                  </span>
                  <span className="font-medium tabular-nums text-green-700">
                    +{formatBRL(row.total)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-border">
          <header className="border-b border-border bg-muted/40 px-4 py-2">
            <h3 className="text-sm font-semibold text-red-700">Despesas</h3>
          </header>
          {data.outflows.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">Sem saídas no período.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.outflows.map((row) => (
                <li key={row.categoryId} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span>
                    {row.categoryName}{' '}
                    <span className="text-xs text-muted-foreground">({row.count})</span>
                  </span>
                  <span className="font-medium tabular-nums text-red-700">
                    −{formatBRL(row.total)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Considera lançamentos do fluxo de caixa com categoria (exclui transferências e estornos).
      </p>
    </div>
  )
}

function FreightSection({ period }: { period: ReportPeriod }) {
  const { data, isLoading } = useFreightProfitability(period)

  const COLUMNS: Column<NonNullable<typeof data>['items'][number]>[] = [
    { key: 'code', header: 'Código', render: (r) => (
      <Link to={`/freights/${r.freightId}`} className="text-primary hover:underline">F-{r.code}</Link>
    ) },
    { key: 'createdAt', header: 'Criado', render: (r) => formatDate(r.createdAt) },
    { key: 'company', header: 'Cliente', render: (r) => r.company.tradeName },
    { key: 'driver', header: 'Motorista', className: 'hidden lg:table-cell', render: (r) => r.driver?.name ?? '—' },
    { key: 'vehicle', header: 'Veículo', className: 'hidden lg:table-cell', render: (r) => r.vehicle?.plate ?? '—' },
    { key: 'revenue', header: 'Receita', render: (r) => <span className="tabular-nums">{formatBRL(r.revenue)}</span> },
    { key: 'driverPay', header: 'Motorista', className: 'hidden md:table-cell', render: (r) => <span className="tabular-nums">{formatBRL(r.driverPay)}</span> },
    { key: 'margin', header: 'Margem', render: (r) => (
      <span className={cn('font-medium tabular-nums', Number.parseFloat(r.margin) >= 0 ? 'text-green-700' : 'text-red-700')}>
        {formatBRL(r.margin)} <span className="text-xs text-muted-foreground">({r.marginPercent}%)</span>
      </span>
    ) },
  ]

  return (
    <DataTable
      columns={COLUMNS}
      data={data?.items ?? []}
      keyExtractor={(r) => r.freightId}
      isLoading={isLoading}
      emptyTitle="Nenhum frete no período"
      emptyDescription="Ajuste o período ou registre fretes com valor e valor do motorista."
    />
  )
}

function DriverSection({ period }: { period: ReportPeriod }) {
  const { data, isLoading } = useDriverProfitability(period)
  const COLUMNS: Column<NonNullable<typeof data>['items'][number]>[] = [
    { key: 'driver', header: 'Motorista', render: (r) => r.driverName },
    { key: 'count', header: 'Fretes', render: (r) => r.freightCount },
    { key: 'revenue', header: 'Receita gerada', render: (r) => <span className="tabular-nums">{formatBRL(r.revenue)}</span> },
    { key: 'driverPay', header: 'Pago ao motorista', render: (r) => <span className="tabular-nums">{formatBRL(r.driverPay)}</span> },
    { key: 'margin', header: 'Margem da empresa', render: (r) => (
      <span className={cn('font-medium tabular-nums', Number.parseFloat(r.margin) >= 0 ? 'text-green-700' : 'text-red-700')}>
        {formatBRL(r.margin)}
      </span>
    ) },
  ]
  return (
    <DataTable
      columns={COLUMNS}
      data={data?.items ?? []}
      keyExtractor={(r) => r.driverId}
      isLoading={isLoading}
      emptyTitle="Nenhum motorista no período"
      emptyDescription="Ajuste o período ou registre fretes atribuídos."
    />
  )
}

function VehicleSection({ period }: { period: ReportPeriod }) {
  const { data, isLoading } = useVehicleProfitability(period)
  const COLUMNS: Column<NonNullable<typeof data>['items'][number]>[] = [
    { key: 'plate', header: 'Placa', render: (r) => (
      <Link to={`/vehicles/${r.vehicleId}`} className="font-mono text-primary hover:underline">{r.plate}</Link>
    ) },
    { key: 'count', header: 'Fretes', render: (r) => r.freightCount },
    { key: 'revenue', header: 'Receita', render: (r) => <span className="tabular-nums">{formatBRL(r.revenue)}</span> },
    { key: 'driverPay', header: 'Motoristas', className: 'hidden lg:table-cell', render: (r) => <span className="tabular-nums">{formatBRL(r.driverPay)}</span> },
    { key: 'expenses', header: 'Despesas', render: (r) => (
      <span className="tabular-nums text-red-700">
        −{formatBRL(r.expensesTotal)}{' '}
        <span className="text-xs text-muted-foreground">({r.expensesCount})</span>
      </span>
    ) },
    { key: 'net', header: 'Resultado', render: (r) => (
      <span className={cn('font-medium tabular-nums', Number.parseFloat(r.netResult) >= 0 ? 'text-green-700' : 'text-red-700')}>
        {formatBRL(r.netResult)}
      </span>
    ) },
  ]
  return (
    <DataTable
      columns={COLUMNS}
      data={data?.items ?? []}
      keyExtractor={(r) => r.vehicleId}
      isLoading={isLoading}
      emptyTitle="Nenhum veículo com movimento"
      emptyDescription="Ajuste o período ou registre fretes/despesas vinculados a veículos."
    />
  )
}

export function ReportsPage() {
  const [tab, setTab] = useState<Tab>('dre')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const period: ReportPeriod = {
    ...(startDate && { startDate: new Date(startDate).toISOString() }),
    ...(endDate && { endDate: new Date(`${endDate}T23:59:59`).toISOString() }),
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Relatórios financeiros</h1>
          <p className="text-sm text-muted-foreground">
            DRE simplificado e lucratividade por frete, motorista e veículo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            className={inputClass}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Data inicial"
          />
          <input
            type="date"
            className={inputClass}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="Data final"
          />
        </div>
      </div>

      <div className="flex overflow-hidden rounded-md border border-input text-sm">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'flex-1 px-4 py-2 transition-colors',
              tab === t.key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted/50',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dre' && <DRESection period={period} />}
      {tab === 'freight' && <FreightSection period={period} />}
      {tab === 'driver' && <DriverSection period={period} />}
      {tab === 'vehicle' && <VehicleSection period={period} />}
    </div>
  )
}
